import React, { useState } from 'react';
import { Calendar, DollarSign, Star, TrendingUp, BarChart3, Package, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useVendorAnalytics } from '../../services/vendorAnalyticsService';
import FormattedDate from '../../components/shared/FormattedDate';

const VendorAnalytics = () => {
  const [period, setPeriod] = useState('month');

  const { data, isLoading, error } = useVendorAnalytics(period);

  const analytics = data?.data || {};

  // Helper function to format numeric values safely
  const formatValue = (value, formatter) => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'N/A';
    }
    return formatter(value);
  };

  // Key metrics cards
  const keyMetrics = [
    {
      label: 'Total Bookings',
      value: analytics.total_bookings || 0,
      icon: Calendar,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      label: 'Total Revenue',
      value: formatValue(analytics.total_revenue, (val) => `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`),
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      label: 'Average Rating',
      value: formatValue(analytics.average_rating, (val) => `${Number(val).toFixed(1)}/5`),
      icon: Star,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      label: 'Total Reviews',
      value: analytics.total_reviews || 0,
      icon: BarChart3,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  // Secondary metrics
  const secondaryMetrics = [
    {
      label: 'Booking Conversion Rate',
      value: formatValue(analytics.booking_conversion_rate, (val) => `${Number(val).toFixed(1)}%`),
      description: 'Percentage of inquiries converted to bookings'
    },
    {
      label: 'Average Booking Value',
      value: formatValue(analytics.average_booking_value, (val) => `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`),
      description: 'Average revenue per completed booking'
    }
  ];

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Analytics</h1>
          <p className="text-sm text-gray-600">Overview of your business performance</p>
        </div>
      </div>

      {/* Period Tabs */}
      <div className="flex space-x-4 border-b border-gray-200 mb-6 overflow-x-auto">
        {[
          { value: 'week', label: 'This Week' },
          { value: 'month', label: 'This Month' },
          { value: 'quarter', label: 'This Quarter' },
          { value: 'year', label: 'This Year' }
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setPeriod(tab.value)}
            className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors duration-200 ${
              period === tab.value
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-32"></div>
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          Failed to load analytics data. Please try again.
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {keyMetrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 ${metric.bgColor} rounded-lg flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 ${metric.color}`} />
                    </div>
                    {metric.change !== null && metric.change !== undefined && !isNaN(metric.change) && (
                      <div className={`flex items-center gap-1 text-sm ${
                        metric.change >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {metric.change >= 0 ? (
                          <ArrowUpRight className="w-4 h-4" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4" />
                        )}
                        <span>{Math.abs(metric.change).toFixed(1)}%</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{metric.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                </div>
              );
            })}
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {secondaryMetrics.map((metric, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                <p className="text-sm text-gray-600 mb-1">{metric.label}</p>
                <p className="text-2xl font-bold text-gray-900 mb-2">{metric.value}</p>
                <p className="text-xs text-gray-500">{metric.description}</p>
              </div>
            ))}
          </div>

          {/* Top Services */}
          {analytics.top_services && analytics.top_services.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Top Performing Services</h2>
              </div>
              <div className="space-y-3">
                {analytics.top_services.map((service, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{service.service_name}</p>
                      <p className="text-sm text-gray-500">{service.booking_count || 0} bookings</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-gray-900">
                        ${Number(service.total_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-gray-500">revenue</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Booking Trends */}
          {analytics.booking_trends && analytics.booking_trends.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Booking Trends</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Period</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Bookings</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.booking_trends.map((trend, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-900">
                          <FormattedDate date={trend.period} format="auto" />
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 text-right">{trend.booking_count || 0}</td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 text-right">
                          ${Number(trend.revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty State */}
          {(!analytics.top_services || analytics.top_services.length === 0) &&
           (!analytics.booking_trends || analytics.booking_trends.length === 0) && (
            <div className="bg-white rounded-lg border border-gray-200 p-8 sm:p-12 text-center">
              <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No analytics data available for this period</p>
              <p className="text-sm text-gray-500 mt-2">Start getting bookings to see your analytics</p>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default VendorAnalytics;
